/**
 * Build-injected debug flag for WebRTC verbose logging.
 *
 * `PLAYCAST_DEBUG_WEBRTC` is replaced at BUILD TIME by each consuming app's vite config
 * (see their vite.config.ts `define`):
 *
 *   - development-mode builds  →  `"1"` (debug build — ALWAYS on)
 *   - production-mode builds   →  `"0"` (release — dead-code-eliminated)
 *   - jest                     →  `"1"` via jest.setup.ts (tests exercise the path)
 *
 * The identifier is read WITHOUT a global prefix because vite/rollup's `define` only
 * substitutes bare identifiers — `globalThis.PLAYCAST_DEBUG_WEBRTC` (bracket access) is
 * NOT replaced and the check would silently never fire in a built app. In a built bundle
 * the literal is inlined (constant-folding makes the debug branches vanish from
 * release); outside a build the `typeof` guard keeps the reference safe. The `?debug`
 * query param stays as the manual escape hatch on any build.
 */
export function isDebugWebrtcLoggingEnabled(fallback = false): boolean {
  // eslint-disable-next-line no-restricted-globals
  if (typeof PLAYCAST_DEBUG_WEBRTC !== 'undefined' && PLAYCAST_DEBUG_WEBRTC === '1') {
    return true;
  }
  if (
    typeof URLSearchParams !== 'undefined' &&
    new URLSearchParams(typeof location === 'undefined' ? '' : location.search).has('debug')
  ) {
    return true;
  }
  if (typeof process !== 'undefined' && process.env?.['PLAYCAST_DEBUG_WEBRTC'] === '1') {
    return true;
  }
  return fallback;
}

/**
 * True only in a debug BUILD (`PLAYCAST_DEBUG_WEBRTC` baked to "1" by a non-production
 * vite mode, or set by jest). Unlike `isDebugWebrtcLoggingEnabled` this honours no `?debug`
 * escape hatch: it gates UI that must never appear on a release build, however the page
 * was reached.
 */
export function isDebugBuild(): boolean {
  // eslint-disable-next-line no-restricted-globals
  return typeof PLAYCAST_DEBUG_WEBRTC !== 'undefined' && PLAYCAST_DEBUG_WEBRTC === '1';
}

/**
 * TypeScript cannot see the build-injected global. Declared here so both the check
 * above and any consumer compile; vite replaces it at build time.
 */
declare global {
  // eslint-disable-next-line no-var
  var PLAYCAST_DEBUG_WEBRTC: string | undefined;
}